import { isAfter, isBefore, parseISO } from 'date-fns';
import {
  Application,
  APPLICATION_STATUS,
  FilteredApplication,
  FilteredOffer,
  isLocalizedString,
  MerchantPaymentProduct,
  Order,
  OrderItem,
  Locale,
  Disclosure,
  GeneratedDisclosures,
  Offer,
} from '@ads-bread/shared/bread/codecs';
import {
  NamespacePath,
  NAMESPACE_MAP,
  APPLICATION_STATUS_CODES,
  hasValidOffers,
  REQUIRED_DISCLOSURES,
} from '@ads-bread/shared/bread/util';
import { totalPriceIsCovered } from '../../lib/application';
import { orderEqual } from '../../lib/order';
import { createLogger } from '../../lib/logger';

const logger = createLogger({ name: 'checkout.api.diagnostic' });

/**
 * Determine if an application is an in store application
 * @param application
 * @returns boolean
 */
export function isUnexpiredInStoreApplication(
  namespace: NamespacePath,
  application: Application,
  isVirtualCard: boolean
): boolean {
  if (namespace !== NAMESPACE_MAP.InStore) {
    return false;
  }
  if (isVirtualCard) {
    const isValid = !!(
      isBefore(new Date(), parseISO(application.expiresAt)) &&
      application.offers?.some(
        (offer) =>
          offer.status === APPLICATION_STATUS.CHECKOUT_PREPARED ||
          offer.status === APPLICATION_STATUS.ATTEMPTED_CHECKOUT
      )
    );

    if (!isValid) {
      logger.debug(
        {
          applicationID: application.id,
          applicationStatus: application.status,
          applicationExpiresAt: application.expiresAt,
          buyerID: application.buyerID,
        },
        'Direct Experience Expired: In store application is expired or does not have a valid offer'
      );
    }
    return isValid;
  } else {
    return !!application.offers?.some(
      (offer) =>
        offer.status === APPLICATION_STATUS.CHECKOUT_PREPARED &&
        offer.shortCode?.value &&
        isBefore(new Date(), parseISO(offer.shortCode.expiresAt))
    );
  }
}

/**
 * Checks an application for ineligible buyer address
 * @param application
 * @returns boolean
 */
export const isIneligibleAddress = (
  application: Application | null
): boolean => {
  return !!application?.statusCodes?.some(
    (statusCode) =>
      APPLICATION_STATUS_CODES.EligibilityIneligibleInvalidAddressPOBox ===
      statusCode
  );
};

/**
 * Determine if an order on an application matches the current order
 * @param validApplication Application
 * @param order Order
 * @returns boolean
 */
export function matchesPreviouslyApproved(
  validApplication: Application,
  order: Order,
  locale: Locale
): boolean {
  return (
    order &&
    totalPriceIsCovered(validApplication, order) &&
    orderEqual(validApplication.order, order, locale)
  );
}

/**
 * Ensures an application is approved with valid offers
 * @param application Application
 * @param merchantPaymentProducts
 * @returns boolean
 */
export function isValidApplication(
  application: Application,
  merchantPaymentProducts: MerchantPaymentProduct[]
): boolean {
  const isValidApp = !!(
    application &&
    application.status === APPLICATION_STATUS.APPROVED &&
    application.offers?.some(
      (offer) =>
        offer.paymentAgreement.status === 'OFFERED' &&
        isBefore(new Date(), parseISO(offer.paymentAgreement.expiresAt))
    )
  );
  const isValidPaymentProducts =
    !!application.offers &&
    hasValidOffers(application.offers, merchantPaymentProducts);

  return isValidApp && isValidPaymentProducts;
}

/**
 * Filters capacity from application if merchant does not have permission to view capacity
 * @param app Application
 * @param canViewCapacity boolean
 * @returns FilteredApplication
 */
export const filterApplicationCapacity = (
  app: Application,
  canViewCapacity: boolean
): FilteredApplication => {
  if (canViewCapacity) {
    return app;
  }
  const capacityToggleOff: FilteredApplication = {
    ...app,
    offers: (app?.offers || []).map((offer) => {
      const appOffer: FilteredOffer = {
        ...offer,
      };
      const { capacity, overflowCapacity, ...filteredOffer } = appOffer;
      return filteredOffer;
    }),
  };

  const { capacity, ...filteredApplication } = capacityToggleOff;

  return filteredApplication;
};

/**
 * Returns shipping descriptions as localized string
 * @param orderItems
 * @param locale
 * @returns OrderItem[]
 */
export function stringifyShippingDescription(
  orderItems: OrderItem[],
  locale: Locale
): OrderItem[] {
  return orderItems.map((item) => ({
    ...item,
    shippingDescription: isLocalizedString(item.shippingDescription)
      ? item.shippingDescription[locale]
      : item.shippingDescription,
  }));
}

/**
 * Checks in store clover offers for an expired short code
 * Note: Exchanged buyers will probably never be clover, but we are checking anyhow
 * @param application
 * @returns boolean
 */
export const hasExpiredCloverShortCode = (
  application: Application,
  isVirtualCard: boolean,
  isExchangedBuyer: boolean
): boolean => {
  return (
    !isExchangedBuyer &&
    !isVirtualCard &&
    !!application.offers?.some((offer) => {
      return (
        offer.shortCode?.value &&
        isAfter(new Date(), parseISO(offer.shortCode.expiresAt))
      );
    })
  );
};

/**
 * Finds required disclosures that need checksums based on the offer's product type
 * and returns them with the correct checksum from the generated disclosures
 * @param offer Offer
 * @param generatedDisclosures GeneratedDisclosures[]
 * @returns Disclosure[]
 */
export const getRequiredDisclosureChecksums = (
  offer: Offer,
  generatedDisclosures: GeneratedDisclosures[]
): Disclosure[] => {
  const requiredDisclosures = REQUIRED_DISCLOSURES[offer.paymentProduct.type];

  if (!requiredDisclosures) {
    return [];
  }

  const findGeneratedDisclosureByName = (
    disclosureName?: string
  ): GeneratedDisclosures | undefined => {
    if (!disclosureName) {
      return undefined;
    }

    return generatedDisclosures.find((generatedDisclosure) =>
      generatedDisclosure.disclosures.some(
        (d) => d.disclosureName === disclosureName
      )
    );
  };

  const resolveChecksumDisclosure = (
    disclosureName: string,
    checksumKey?: string
  ): GeneratedDisclosures | undefined => {
    return (
      findGeneratedDisclosureByName(disclosureName) ||
      findGeneratedDisclosureByName(checksumKey)
    );
  };

  const disclosuresToInclude: Disclosure[] = [];
  for (const requiredDisclosure of requiredDisclosures) {
    if (requiredDisclosure.requiresChecksum) {
      const checksumDisclosure = resolveChecksumDisclosure(
        requiredDisclosure.disclosureName,
        requiredDisclosure.checksumKey
      );

      if (!checksumDisclosure) {
        // If a checksum is required but we don't have a generated disclosure for it
        // or its fallback checksum key, we should not include this disclosure.
        continue;
      }

      disclosuresToInclude.push({
        type: requiredDisclosure.disclosureName,
        checksum: checksumDisclosure.disclosureRef,
      });
      continue;
    }

    disclosuresToInclude.push({
      type: requiredDisclosure.disclosureName,
    });
  }

  return disclosuresToInclude;
};
